import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { PageHeader, Panel, PageLoader, StatusBadge } from "@/lib/ui";
import { Mail, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS_META = {
  live: { tone: "ok", label: "Live" },
  clerk: { tone: "muted", label: "Clerk" },
  website: { tone: "warn", label: "Website" },
};

function Preview({ tpl }) {
  const api = useApi();
  const { data, isLoading } = useQuery({
    queryKey: ["email-preview", tpl.key],
    queryFn: () => api.get(`/emails/preview/${tpl.key}`),
  });
  if (isLoading) return <div className="h-full flex items-center justify-center"><PageLoader /></div>;
  return (
    <iframe
      title={tpl.name}
      srcDoc={data?.html || ""}
      data-testid={`email-preview-${tpl.key}`}
      className="w-full h-full border-0 rounded-lg bg-white"
    />
  );
}

export default function EmailTemplates() {
  const api = useApi();
  const { data: templates = [], isLoading } = useQuery({ queryKey: ["email-templates"], queryFn: () => api.get("/emails/templates") });
  const [active, setActive] = useState(null);

  if (isLoading) return <PageLoader />;
  const selected = active || templates[0];

  return (
    <div data-testid="email-templates-page">
      <PageHeader kicker="Content · Brand" title="Email Templates">
        <StatusBadge tone="gold" dot>{templates.length} templates</StatusBadge>
      </PageHeader>

      <div className="grid lg:grid-cols-[300px_1fr] gap-4">
        <Panel className="p-2 h-fit">
          <div className="space-y-1 max-h-[70vh] overflow-y-auto" data-testid="email-template-list">
            {templates.map((t) => {
              const sm = STATUS_META[t.status] || STATUS_META.website;
              const isSel = selected?.key === t.key;
              return (
                <button key={t.key} onClick={() => setActive(t)} data-testid={`email-template-${t.key}`}
                  className={cn("w-full text-left rounded-md px-3 py-2.5 transition-colors flex items-center gap-3",
                    isSel ? "bg-primary/10 border border-primary/40" : "hover:bg-accent/40 border border-transparent")}>
                  <Mail className={cn("h-4 w-4 shrink-0", isSel ? "text-primary" : "text-muted-foreground")} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="text-[11px] text-muted-foreground">{t.category}</div>
                  </div>
                  <StatusBadge tone={sm.tone}>{sm.label}</StatusBadge>
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel className="p-3 flex flex-col" style={{ height: "78vh" }}>
          {selected ? (
            <>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{selected.name}</span>
                  <span className="text-xs text-muted-foreground">· {selected.category}</span>
                </div>
                {selected.status === "clerk" && <span className="text-[11px] text-muted-foreground">Delivered by Clerk (auth)</span>}
                {selected.status === "website" && <span className="text-[11px] text-muted-foreground">For the GGG website workflow</span>}
                {selected.status === "live" && <span className="text-[11px] text-ok">Wired & sending live</span>}
              </div>
              <div className="flex-1 min-h-0"><Preview tpl={selected} /></div>
            </>
          ) : null}
        </Panel>
      </div>
    </div>
  );
}
